<template>
  <div class="max-w-7xl mx-auto pb-10">

    <div class="space-y-6 animate-fade-in">

      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Existencias y Kardex</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm">Monitoreo de lotes, costos y movimientos en tiempo real.</p>
        </div>
        <div class="flex gap-2">
          <button @click="loadData" class="p-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 text-slate-500 transition-all">
            <RefreshCw class="w-5 h-5" :class="{ 'animate-spin': isLoading }" />
          </button>
          <button class="flex items-center gap-2 px-5 py-2.5 bg-slate-900 dark:bg-white dark:text-slate-900 hover:bg-slate-700 text-white font-bold rounded-xl shadow-lg transition-all active:scale-95">
            <Download class="w-5 h-5" /> Exportar
          </button>
        </div>
      </div>

      <div class="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row gap-4">
        <div class="relative flex-1">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Buscar producto, código o lote..."
            class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
          >
        </div>
        <div class="w-full sm:w-64">
          <div class="relative">
            <Building class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select v-model="filterLocation" class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 appearance-none">
              <option value="">Todas las Bodegas</option>
              <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
            </select>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              <tr>
                <th class="px-6 py-4 font-semibold">Producto</th>
                <th class="px-6 py-4 font-semibold">Bodega</th>
                <th class="px-6 py-4 font-semibold">Lote / Vencimiento</th>
                <th class="px-6 py-4 font-semibold text-right">Cantidad</th>
                <th class="px-6 py-4 font-semibold text-right">Costo Prom.</th>
                <th class="px-6 py-4 font-semibold text-right">Total Valor</th>
                <th class="px-6 py-4 text-right">Kardex</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
              <tr v-if="isLoading">
                <td colspan="7" class="px-6 py-10 text-center text-slate-500">
                  <div class="flex justify-center items-center gap-2">
                    <Loader2 class="w-5 h-5 animate-spin" /> Cargando inventario...
                  </div>
                </td>
              </tr>
              <tr v-else-if="filteredList.length === 0">
                <td colspan="7" class="px-6 py-10 text-center text-slate-500">No hay existencias con estos filtros.</td>
              </tr>
              <tr v-else v-for="item in filteredList" :key="item.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
                <td class="px-6 py-4">
                  <div class="font-bold text-slate-900 dark:text-slate-100">{{ productsMap[item.product]?.name || 'Producto Desconocido' }}</div>
                  <div class="text-xs font-mono text-slate-500 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded w-fit mt-1">
                    {{ productsMap[item.product]?.code || '---' }}
                  </div>
                </td>
                <td class="px-6 py-4 text-slate-600 dark:text-slate-400">
                  <div class="flex items-center gap-1.5">
                    <MapPin class="w-3.5 h-3.5" />
                    {{ locationsMap[item.location] || 'Bodega General' }}
                  </div>
                </td>
                <td class="px-6 py-4">
                  <div class="flex flex-col gap-1">
                    <span class="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                      <Hash class="w-3 h-3 text-slate-400" /> {{ item.batch_code }}
                    </span>
                    <span v-if="item.expiration_date" class="text-[10px] px-2 py-0.5 rounded-full w-fit"
                      :class="isExpired(item.expiration_date) ? 'bg-red-100 text-red-700' : 'bg-green-50 text-green-700'">
                      Exp: {{ formatDate(item.expiration_date) }}
                    </span>
                  </div>
                </td>
                <td class="px-6 py-4 text-right">
                  <span class="font-bold text-lg" :class="Number(item.quantity) <= 5 ? 'text-amber-600' : 'text-slate-900 dark:text-white'">
                    {{ Number(item.quantity).toFixed(2) }}
                  </span>
                  <span class="text-xs text-slate-400 ml-1">Und</span>
                </td>
                <td class="px-6 py-4 text-right font-mono text-slate-600 dark:text-slate-400 text-xs">
                  ${{ Number(item.cost).toFixed(4) }}
                </td>
                <td class="px-6 py-4 text-right font-bold text-emerald-600 dark:text-emerald-400">
                  ${{ (Number(item.quantity) * Number(item.cost)).toFixed(2) }}
                </td>
                <td class="px-6 py-4 text-right">
                  <button @click="openKardex(item)" class="text-brand-600 hover:text-brand-700 hover:bg-brand-50 p-2 rounded-lg transition-colors" title="Ver movimientos">
                    <FileClock class="w-5 h-5" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="showHistoryModal" class="fixed inset-0 z-[9999] flex items-center justify-center p-4">

        <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" @click="closeKardex"></div>

        <div class="bg-white dark:bg-slate-900 w-full max-w-4xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[85vh] relative z-10 animate-fade-in-up">

          <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800">
            <div>
              <h3 class="font-bold text-lg text-slate-900 dark:text-white">Kardex de Movimientos</h3>
              <p class="text-sm text-slate-500">
                {{ selectedBatch?.productName }} - Lote: {{ selectedBatch?.batch_code }}
              </p>
            </div>
            <button @click="closeKardex" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full text-slate-500">
              <X class="w-5 h-5" />
            </button>
          </div>

          <div class="flex-1 overflow-y-auto custom-scrollbar p-0">
            <table class="w-full text-sm text-left">
              <thead class="bg-slate-50 dark:bg-slate-800/50 text-xs uppercase text-slate-500 sticky top-0 backdrop-blur-md">
                <tr>
                  <th class="px-6 py-3">Fecha</th>
                  <th class="px-6 py-3">Tipo Movimiento</th>
                  <th class="px-6 py-3">Referencia</th>
                  <th class="px-6 py-3 text-right">Entrada</th>
                  <th class="px-6 py-3 text-right">Salida</th>
                  <th class="px-6 py-3 text-right">Costo</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
                <tr v-if="loadingHistory">
                  <td colspan="6" class="p-8 text-center"><Loader2 class="w-6 h-6 animate-spin mx-auto text-brand-500"/></td>
                </tr>
                <tr v-else-if="historyList.length === 0">
                  <td colspan="6" class="p-8 text-center text-slate-500">Este lote no tiene historial registrado (Data antigua).</td>
                </tr>
                <tr v-else v-for="move in historyList" :key="move.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                  <td class="px-6 py-3 whitespace-nowrap text-slate-500 text-xs">
                    {{ new Date(move.created_at).toLocaleString() }}
                  </td>
                  <td class="px-6 py-3">
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
                      :class="getMoveBadge(move.type)">
                      {{ getMoveLabel(move.type) }}
                    </span>
                  </td>
                  <td class="px-6 py-3">
                    <div class="text-xs font-medium text-slate-700 dark:text-slate-300">{{ move.reference_code }}</div>
                    <div class="text-[10px] text-slate-400 truncate max-w-[150px]">{{ move.description }}</div>
                  </td>
                  <td class="px-6 py-3 text-right font-medium text-emerald-600">
                    <span v-if="isPositiveMove(move.type)">+{{ Number(move.quantity).toFixed(2) }}</span>
                    <span v-else class="text-slate-300">-</span>
                  </td>
                  <td class="px-6 py-3 text-right font-medium text-red-600">
                    <span v-if="!isPositiveMove(move.type)">-{{ Number(move.quantity).toFixed(2) }}</span>
                    <span v-else class="text-slate-300">-</span>
                  </td>
                  <td class="px-6 py-3 text-right font-mono text-xs text-slate-500">
                    ${{ Number(move.cost).toFixed(2) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Teleport>

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import {
  Search, RefreshCw, Download, Building, MapPin, Hash, FileClock,
  Loader2, X
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';

const list = ref([]);
const locations = ref([]);
const productsMap = ref({});
const locationsMap = ref({});
const isLoading = ref(true);
const searchTerm = ref('');
const filterLocation = ref('');
const showHistoryModal = ref(false);
const loadingHistory = ref(false);
const historyList = ref([]);
const selectedBatch = ref(null);

const loadData = async () => {
  isLoading.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    const companyId = user?.company;

    const [resBatches, resProds, resLocs] = await Promise.all([
      apiClient.get(`/inventory_batches?company=${companyId}`),
      apiClient.get(`/products?company=${companyId}`),
      apiClient.get(`/inventory_locations?company=${companyId}`) // CORREGIDO: /locations
    ]);

    const prods = resProds.data || [];
    prods.forEach(p => productsMap.value[p.id] = { name: p.name, code: p.code });

    const locs = resLocs.data || [];
    locs.forEach(l => locationsMap.value[l.id] = l.name);
    locations.value = locs;

    list.value = resBatches.data || [];

  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadData);

const filteredList = computed(() => {
  return list.value.filter(item => {
    const prod = productsMap.value[item.product] || {};
    const matchesSearch =
      (prod.name || '').toLowerCase().includes(searchTerm.value.toLowerCase()) ||
      (prod.code || '').toLowerCase().includes(searchTerm.value.toLowerCase()) ||
      (item.batch_code || '').toLowerCase().includes(searchTerm.value.toLowerCase());

    const matchesLoc = filterLocation.value ? item.location == filterLocation.value : true;
    return matchesSearch && matchesLoc;
  });
});

const openKardex = async (batchItem) => {
  selectedBatch.value = {
    ...batchItem,
    productName: productsMap.value[batchItem.product]?.name
  };
  showHistoryModal.value = true;
  loadingHistory.value = true;
  historyList.value = [];

  try {
    const res = await apiClient.get(`/inventory_movements?product=${batchItem.product}&location=${batchItem.location}`);
    const movements = res.data || [];
    historyList.value = movements.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  } catch (e) {
    console.error("Error cargando historial", e);
  } finally {
    loadingHistory.value = false;
  }
};

const closeKardex = () => {
  showHistoryModal.value = false;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString();
};

const isExpired = (dateString) => {
  if (!dateString) return false;
  return new Date(dateString) < new Date();
};

const isPositiveMove = (type) => {
  return ['PURCHASE', 'TRANSFER_IN', 'ADJUSTMENT_ADD', 'SALE_RETURN'].includes(type);
};

const getMoveLabel = (type) => {
  const map = {
    'PURCHASE': 'Compra',
    'SALE': 'Venta',
    'TRANSFER_IN': 'Traslado (Entrada)',
    'TRANSFER_OUT': 'Traslado (Salida)',
    'ADJUSTMENT_ADD': 'Ajuste (+)',
    'ADJUSTMENT_SUB': 'Ajuste (-)'
  };
  return map[type] || type;
};

const getMoveBadge = (type) => {
  if (type === 'PURCHASE') return 'bg-blue-100 text-blue-700';
  if (type === 'SALE') return 'bg-green-100 text-green-700';
  if (type.includes('ADJUSTMENT')) return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-600';
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fade-in 0.4s ease-out forwards;
}

@keyframes fade-in-up {
  from { opacity: 0; transform: scale(0.95) translateY(10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}
.animate-fade-in-up {
  animation: fade-in-up 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
</style>